/**
 * GRANULLO PRE-LIVE EXPANSION — Programmable Policy Context
 * Provides programmable policy infrastructure for CBDC simulation
 * 
 * ARCHITECTURE CONSTRAINT: This composes the existing simulation engine
 * without modifying core systems (Cascade, Exposure, Simulation, Lens, Glass)
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// Program Sponsor Types
export const PROGRAM_SPONSOR_TYPES = {
  GOVERNMENT: 'government',
  CORPORATION: 'corporation',
  VENTURE_FUND: 'venture_fund',
  CSR_FOUNDATION: 'csr_foundation',
  CLIMATE_FUND: 'climate_fund'
};

// Merchant Category Codes (MCC)
export const MERCHANT_CATEGORIES = {
  RESTAURANTS: { code: '5812', name: 'Restaurants', icon: 'utensils' },
  GROCERY: { code: '5411', name: 'Grocery Stores', icon: 'shopping-cart' },
  HEALTHCARE: { code: '8011', name: 'Healthcare', icon: 'heart-pulse' },
  EDUCATION: { code: '8211', name: 'Education', icon: 'graduation-cap' },
  TRANSPORTATION: { code: '4111', name: 'Transportation', icon: 'car' },
  UTILITIES: { code: '4900', name: 'Utilities', icon: 'zap' },
  AGRICULTURE: { code: '0780', name: 'Agriculture', icon: 'wheat' },
  GREEN_ENERGY: { code: '4911', name: 'Green Energy', icon: 'leaf' },
  TECHNOLOGY: { code: '7372', name: 'Technology', icon: 'cpu' },
  WELLNESS: { code: '7991', name: 'Wellness', icon: 'activity' }
};

// Programmable Policy Scenarios
export const POLICY_SCENARIOS = {
  // Government Programs
  AGRICULTURAL_SUBSIDY: {
    id: 'agricultural_subsidy',
    name: 'Agricultural Subsidy',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.GOVERNMENT,
      name: 'Ministry of Agriculture',
      identity: 'GOV-AGRI-001'
    },
    budget: 500,
    beneficiaryGroup: 'Registered Farmers',
    beneficiaryCount: 50000,
    merchantRestrictions: ['0780', '5411', '4900'], // Agriculture, Grocery, Utilities
    geographicConstraints: ['Rural Districts'],
    spendingLimits: { perTransaction: 10, monthly: 50 },
    expiryDays: 90,
    description: 'Direct benefit transfer to farmers for agricultural inputs and supplies',
    cascadeSignals: ['Rural economic activity', 'Agricultural supply chain', 'Local merchant growth']
  },
  
  DISASTER_RELIEF: {
    id: 'disaster_relief',
    name: 'Disaster Relief Payments',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.GOVERNMENT,
      name: 'National Disaster Authority',
      identity: 'GOV-NDMA-001'
    },
    budget: 1000,
    beneficiaryGroup: 'Affected Households',
    beneficiaryCount: 100000,
    merchantRestrictions: ['5411', '5812', '8011', '4900'], // Grocery, Restaurants, Healthcare, Utilities
    geographicConstraints: ['Disaster Zones'],
    spendingLimits: { perTransaction: 25, monthly: 100 },
    expiryDays: 30,
    description: 'Emergency financial assistance for disaster-affected populations',
    cascadeSignals: ['Emergency services activation', 'Local economy stabilization', 'Supply chain stress']
  },
  
  HEALTHCARE_ASSISTANCE: {
    id: 'healthcare_assistance',
    name: 'Healthcare Assistance',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.GOVERNMENT,
      name: 'Ministry of Health',
      identity: 'GOV-HEALTH-001'
    },
    budget: 750,
    beneficiaryGroup: 'Low-Income Families',
    beneficiaryCount: 75000,
    merchantRestrictions: ['8011', '5912'], // Healthcare, Pharmacies
    geographicConstraints: null,
    spendingLimits: { perTransaction: 50, monthly: 200 },
    expiryDays: 365,
    description: 'Healthcare expense coverage for eligible families',
    cascadeSignals: ['Healthcare utilization', 'Pharmacy network activity', 'Health outcomes improvement']
  },
  
  TARGETED_STIMULUS: {
    id: 'targeted_stimulus',
    name: 'Targeted Stimulus',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.GOVERNMENT,
      name: 'Economic Affairs Ministry',
      identity: 'GOV-ECON-001'
    },
    budget: 2000,
    beneficiaryGroup: 'Small Business Owners',
    beneficiaryCount: 200000,
    merchantRestrictions: null, // Open spending
    geographicConstraints: null,
    spendingLimits: { perTransaction: 100, monthly: 500 },
    expiryDays: 60,
    description: 'Economic stimulus for small businesses to boost recovery',
    cascadeSignals: ['Small business activity', 'Employment signals', 'Local economic multiplier']
  },
  
  // Corporate Employee Benefits
  EMPLOYEE_MEAL_ALLOWANCE: {
    id: 'employee_meal_allowance',
    name: 'Employee Meal Allowance',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CORPORATION,
      name: 'Axis Bank Corporate',
      identity: 'CORP-AXIS-001'
    },
    budget: 50,
    beneficiaryGroup: 'Corporate Employees',
    beneficiaryCount: 5000,
    merchantRestrictions: ['5812', '5814'], // Restaurants, Fast Food
    geographicConstraints: ['Office Locations'],
    spendingLimits: { perTransaction: 0.5, monthly: 10 },
    expiryDays: 30,
    description: 'Monthly meal allowance for employees at approved restaurants',
    cascadeSignals: ['Restaurant revenue', 'Employee satisfaction', 'Local F&B ecosystem']
  },
  
  EMPLOYEE_WELLNESS: {
    id: 'employee_wellness',
    name: 'Wellness Benefits',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CORPORATION,
      name: 'Tech Corp India',
      identity: 'CORP-TECH-001'
    },
    budget: 30,
    beneficiaryGroup: 'Corporate Employees',
    beneficiaryCount: 3000,
    merchantRestrictions: ['7991', '7941'], // Wellness, Sports Facilities
    geographicConstraints: null,
    spendingLimits: { perTransaction: 2, monthly: 5 },
    expiryDays: 365,
    description: 'Wellness and fitness benefit program for employee health',
    cascadeSignals: ['Fitness industry growth', 'Healthcare cost reduction', 'Productivity signals']
  },
  
  EMPLOYEE_EDUCATION: {
    id: 'employee_education',
    name: 'Education Credits',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CORPORATION,
      name: 'Global Services Ltd',
      identity: 'CORP-GS-001'
    },
    budget: 100,
    beneficiaryGroup: 'Employees & Dependents',
    beneficiaryCount: 8000,
    merchantRestrictions: ['8211', '8299', '5942'], // Education, Schools, Bookstores
    geographicConstraints: null,
    spendingLimits: { perTransaction: 10, monthly: 25 },
    expiryDays: 365,
    description: 'Education support for employees and their families',
    cascadeSignals: ['Education sector activity', 'Skill development', 'Long-term productivity']
  },
  
  EMPLOYEE_TRANSPORT: {
    id: 'employee_transport',
    name: 'Transportation Subsidy',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CORPORATION,
      name: 'Manufacturing Corp',
      identity: 'CORP-MFG-001'
    },
    budget: 40,
    beneficiaryGroup: 'Factory Workers',
    beneficiaryCount: 4000,
    merchantRestrictions: ['4111', '4121', '5541'], // Transport, Taxis, Fuel
    geographicConstraints: ['Industrial Zones'],
    spendingLimits: { perTransaction: 0.3, monthly: 8 },
    expiryDays: 30,
    description: 'Daily transport allowance for commuting workers',
    cascadeSignals: ['Transport utilization', 'Fuel consumption patterns', 'Worker mobility']
  },
  
  // VC/PE/Startup Programs
  STARTUP_OPERATING_GRANT: {
    id: 'startup_operating_grant',
    name: 'Startup Operating Grant',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.VENTURE_FUND,
      name: 'Sequoia India',
      identity: 'VC-SEQ-001'
    },
    budget: 200,
    beneficiaryGroup: 'Portfolio Startups',
    beneficiaryCount: 50,
    merchantRestrictions: ['7372', '7379', '7311'], // Software, Computer Services, Advertising
    geographicConstraints: ['Startup Hubs'],
    spendingLimits: { perTransaction: 10, monthly: 100 },
    expiryDays: 180,
    milestoneRequired: true,
    description: 'Operating capital for early-stage startups with milestone gates',
    cascadeSignals: ['Startup ecosystem growth', 'Tech hiring signals', 'Innovation cluster formation']
  },
  
  INNOVATION_ECOSYSTEM: {
    id: 'innovation_ecosystem',
    name: 'Innovation Ecosystem Fund',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.VENTURE_FUND,
      name: 'Accel Partners',
      identity: 'VC-ACC-001'
    },
    budget: 500,
    beneficiaryGroup: 'Approved Vendor Network',
    beneficiaryCount: 200,
    merchantRestrictions: ['7372', '8732', '8733'], // Software, Research Labs
    geographicConstraints: null,
    spendingLimits: { perTransaction: 50, monthly: 250 },
    expiryDays: 365,
    milestoneRequired: true,
    description: 'Ecosystem development funding for innovation infrastructure',
    cascadeSignals: ['R&D activity increase', 'Patent filings', 'Technology transfer']
  },
  
  // CSR Programs
  CSR_EDUCATION_GRANTS: {
    id: 'csr_education_grants',
    name: 'Education Grants',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CSR_FOUNDATION,
      name: 'Infosys Foundation',
      identity: 'CSR-INF-001'
    },
    budget: 150,
    beneficiaryGroup: 'Underprivileged Students',
    beneficiaryCount: 15000,
    merchantRestrictions: ['8211', '5942', '5943'], // Schools, Bookstores, Stationery
    geographicConstraints: ['Rural Districts', 'Underserved Areas'],
    spendingLimits: { perTransaction: 2, monthly: 10 },
    expiryDays: 365,
    description: 'Educational support for underprivileged children',
    cascadeSignals: ['School enrollment increase', 'Educational outcomes', 'Community development']
  },
  
  CSR_RURAL_DEVELOPMENT: {
    id: 'csr_rural_development',
    name: 'Rural Development Initiative',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CSR_FOUNDATION,
      name: 'Tata Trusts',
      identity: 'CSR-TATA-001'
    },
    budget: 300,
    beneficiaryGroup: 'Rural Households',
    beneficiaryCount: 30000,
    merchantRestrictions: ['0780', '5411', '5231', '5251'], // Agriculture, Grocery, Hardware
    geographicConstraints: ['Rural Villages'],
    spendingLimits: { perTransaction: 5, monthly: 20 },
    expiryDays: 180,
    description: 'Comprehensive rural development and livelihood support',
    cascadeSignals: ['Rural economic uplift', 'Infrastructure improvement', 'Migration reduction']
  },
  
  CSR_WOMEN_EMPOWERMENT: {
    id: 'csr_women_empowerment',
    name: 'Women Empowerment Program',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CSR_FOUNDATION,
      name: 'Azim Premji Foundation',
      identity: 'CSR-APF-001'
    },
    budget: 100,
    beneficiaryGroup: 'Women Entrepreneurs',
    beneficiaryCount: 10000,
    merchantRestrictions: null, // Business-related spending
    geographicConstraints: null,
    spendingLimits: { perTransaction: 5, monthly: 25 },
    expiryDays: 365,
    description: 'Microenterprise support for women-led businesses',
    cascadeSignals: ['Women entrepreneurship', 'Household income increase', 'Community resilience']
  },
  
  // Climate & Sustainability
  RENEWABLE_ENERGY_INCENTIVE: {
    id: 'renewable_energy_incentive',
    name: 'Renewable Energy Incentives',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CLIMATE_FUND,
      name: 'Green Climate Fund India',
      identity: 'CLM-GCF-001'
    },
    budget: 400,
    beneficiaryGroup: 'Households & Businesses',
    beneficiaryCount: 40000,
    merchantRestrictions: ['4911', '5074', '1711'], // Green Energy, Electrical Equipment
    geographicConstraints: null,
    spendingLimits: { perTransaction: 20, monthly: 50 },
    expiryDays: 365,
    milestoneRequired: true,
    description: 'Subsidies for solar panel installation and renewable adoption',
    cascadeSignals: ['Renewable capacity growth', 'Grid load reduction', 'Carbon emission decrease']
  },
  
  SUSTAINABLE_AGRICULTURE: {
    id: 'sustainable_agriculture',
    name: 'Sustainable Agriculture Support',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CLIMATE_FUND,
      name: 'World Bank Climate Initiative',
      identity: 'CLM-WB-001'
    },
    budget: 250,
    beneficiaryGroup: 'Organic Farmers',
    beneficiaryCount: 25000,
    merchantRestrictions: ['0780', '5191', '5261'], // Agriculture, Farm Supplies
    geographicConstraints: ['Agricultural Zones'],
    spendingLimits: { perTransaction: 10, monthly: 40 },
    expiryDays: 180,
    milestoneRequired: true,
    description: 'Incentives for sustainable and organic farming practices',
    cascadeSignals: ['Organic farming adoption', 'Soil health improvement', 'Water conservation']
  },
  
  CARBON_REDUCTION_REWARD: {
    id: 'carbon_reduction_reward',
    name: 'Carbon Reduction Rewards',
    sponsor: {
      type: PROGRAM_SPONSOR_TYPES.CLIMATE_FUND,
      name: 'Clean Energy Alliance',
      identity: 'CLM-CEA-001'
    },
    budget: 200,
    beneficiaryGroup: 'Green-Certified Businesses',
    beneficiaryCount: 5000,
    merchantRestrictions: ['4911', '4931', '7389'], // Energy, Waste Management
    geographicConstraints: null,
    spendingLimits: { perTransaction: 50, monthly: 200 },
    expiryDays: 365,
    milestoneRequired: true,
    description: 'Performance-based rewards for verified carbon reduction',
    cascadeSignals: ['Carbon footprint reduction', 'Green certification adoption', 'ESG compliance']
  }
};

// Scenario Categories for UI grouping
export const SCENARIO_CATEGORIES = {
  GOVERNMENT: {
    name: 'Government Programs',
    icon: 'landmark',
    color: '#a371f7',
    scenarios: ['AGRICULTURAL_SUBSIDY', 'DISASTER_RELIEF', 'HEALTHCARE_ASSISTANCE', 'TARGETED_STIMULUS']
  },
  EMPLOYEE: {
    name: 'Employee Benefits',
    icon: 'briefcase',
    color: '#58a6ff',
    scenarios: ['EMPLOYEE_MEAL_ALLOWANCE', 'EMPLOYEE_WELLNESS', 'EMPLOYEE_EDUCATION', 'EMPLOYEE_TRANSPORT']
  },
  VENTURE: {
    name: 'VC/PE/Startup',
    icon: 'rocket',
    color: '#f0883e',
    scenarios: ['STARTUP_OPERATING_GRANT', 'INNOVATION_ECOSYSTEM']
  },
  CSR: {
    name: 'CSR Programs',
    icon: 'heart',
    color: '#3fb950',
    scenarios: ['CSR_EDUCATION_GRANTS', 'CSR_RURAL_DEVELOPMENT', 'CSR_WOMEN_EMPOWERMENT']
  },
  CLIMATE: {
    name: 'Climate & Sustainability',
    icon: 'leaf',
    color: '#39d353',
    scenarios: ['RENEWABLE_ENERGY_INCENTIVE', 'SUSTAINABLE_AGRICULTURE', 'CARBON_REDUCTION_REWARD']
  }
};

// Bank Validation Steps
export const VALIDATION_STEPS = {
  ELIGIBILITY: { id: 'eligibility', name: 'Eligibility Check', icon: 'user-check' },
  MERCHANT_MCC: { id: 'merchant_mcc', name: 'Merchant MCC Validation', icon: 'store' },
  POLICY_RULES: { id: 'policy_rules', name: 'Policy Rule Enforcement', icon: 'shield-check' },
  BALANCE: { id: 'balance', name: 'Balance Verification', icon: 'wallet' },
  EXPIRY: { id: 'expiry', name: 'Expiry Validation', icon: 'clock' },
  GEOGRAPHIC: { id: 'geographic', name: 'Geographic Constraint', icon: 'map-pin' }
};

// Transaction Validation Result
export const VALIDATION_RESULTS = {
  APPROVED: 'approved',
  REJECTED_ELIGIBILITY: 'rejected_eligibility',
  REJECTED_MERCHANT: 'rejected_merchant',
  REJECTED_LIMIT: 'rejected_limit',
  REJECTED_EXPIRY: 'rejected_expiry',
  REJECTED_GEOGRAPHIC: 'rejected_geographic',
  REJECTED_BALANCE: 'rejected_balance'
};

// Create Context
const ProgrammablePolicyContext = createContext(null);

export function ProgrammablePolicyProvider({ children }) {
  // Active Policy State
  const [activeScenario, setActiveScenario] = useState(null);
  const [policyParameters, setPolicyParameters] = useState({
    budget: 500,
    beneficiaryCount: 10000,
    networkCongestion: 20,
    transactionLatency: 100,
    policyStrictness: 50,
    merchantNetworkSize: 100
  });
  
  // Validation State
  const [validationTrace, setValidationTrace] = useState([]);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  
  // Audit Metrics (Central Bank View)
  const [auditMetrics, setAuditMetrics] = useState({
    totalIssuance: 0,
    programCoverage: 0,
    violationsBlocked: 0,
    distributionEfficiency: 0,
    systemicExposure: 0,
    programPerformance: 0
  });
  
  // Cascade Impact State
  const [cascadeImpact, setCascadeImpact] = useState({
    economicRipple: [],
    networkActivityChange: 0,
    exposureAdjustment: 0
  });
  
  // Select Scenario
  const selectScenario = useCallback((scenarioKey) => {
    const scenario = POLICY_SCENARIOS[scenarioKey];
    if (scenario) {
      setActiveScenario(scenario);
      setPolicyParameters(prev => ({
        ...prev,
        budget: scenario.budget,
        beneficiaryCount: scenario.beneficiaryCount
      }));
      // Reset validation trace
      setValidationTrace([]);
      setCurrentTransaction(null);
    }
  }, []);
  
  // Simulate Transaction Validation
  const simulateValidation = useCallback((transaction) => {
    if (!activeScenario) return null;
    
    setCurrentTransaction(transaction);
    const trace = [];
    let result = VALIDATION_RESULTS.APPROVED;
    
    // Step 1: Eligibility Check
    trace.push({
      step: VALIDATION_STEPS.ELIGIBILITY,
      status: 'processing',
      timestamp: Date.now()
    });
    
    // Simulate eligibility (random for demo)
    const eligibilityPass = Math.random() > 0.1;
    trace[0].status = eligibilityPass ? 'passed' : 'failed';
    if (!eligibilityPass) {
      result = VALIDATION_RESULTS.REJECTED_ELIGIBILITY;
      setValidationTrace(trace);
      return { result, trace, reason: 'Beneficiary not eligible for this program' };
    }
    
    // Step 2: Merchant MCC Validation
    trace.push({
      step: VALIDATION_STEPS.MERCHANT_MCC,
      status: 'processing',
      timestamp: Date.now()
    });
    
    const merchantPass = !activeScenario.merchantRestrictions || 
      activeScenario.merchantRestrictions.includes(transaction.merchantMCC);
    trace[1].status = merchantPass ? 'passed' : 'failed';
    if (!merchantPass) {
      result = VALIDATION_RESULTS.REJECTED_MERCHANT;
      setValidationTrace(trace);
      return { result, trace, reason: `Merchant category ${transaction.merchantMCC} not allowed` };
    }
    
    // Step 3: Policy Rule Enforcement
    trace.push({
      step: VALIDATION_STEPS.POLICY_RULES,
      status: 'processing',
      timestamp: Date.now()
    });
    
    const amountPass = transaction.amount <= activeScenario.spendingLimits.perTransaction;
    trace[2].status = amountPass ? 'passed' : 'failed';
    if (!amountPass) {
      result = VALIDATION_RESULTS.REJECTED_LIMIT;
      setValidationTrace(trace);
      return { result, trace, reason: `Amount exceeds per-transaction limit of ${activeScenario.spendingLimits.perTransaction}M` };
    }
    
    // Step 4: Balance Verification
    trace.push({
      step: VALIDATION_STEPS.BALANCE,
      status: 'processing',
      timestamp: Date.now()
    });
    trace[3].status = 'passed';
    
    // Step 5: Expiry Validation
    trace.push({
      step: VALIDATION_STEPS.EXPIRY,
      status: 'processing',
      timestamp: Date.now()
    });
    trace[4].status = 'passed';
    
    setValidationTrace(trace);
    return { result, trace, reason: 'Transaction approved' };
  }, [activeScenario]);
  
  // Update Audit Metrics
  const updateAuditMetrics = useCallback((simulationMetrics) => {
    if (!activeScenario) return;
    
    setAuditMetrics({
      totalIssuance: simulationMetrics.totalIssuance || activeScenario.budget,
      programCoverage: Math.min(100, Math.round((simulationMetrics.networkCoverage || 0) * 1.1)),
      violationsBlocked: Math.floor(Math.random() * 10) + 2,
      distributionEfficiency: Math.round(85 + Math.random() * 12),
      systemicExposure: simulationMetrics.cascadeRisk || 0,
      programPerformance: Math.round(75 + Math.random() * 20)
    });
  }, [activeScenario]);
  
  // Calculate Cascade Impact
  const calculateCascadeImpact = useCallback((simulationResults) => {
    if (!activeScenario) return;
    
    setCascadeImpact({
      economicRipple: activeScenario.cascadeSignals || [],
      networkActivityChange: Math.round(15 + Math.random() * 25),
      exposureAdjustment: Math.round(5 + Math.random() * 15)
    });
  }, [activeScenario]);
  
  // Reset Policy State
  const resetPolicy = useCallback(() => {
    setActiveScenario(null);
    setValidationTrace([]);
    setCurrentTransaction(null);
    setAuditMetrics({
      totalIssuance: 0,
      programCoverage: 0,
      violationsBlocked: 0,
      distributionEfficiency: 0,
      systemicExposure: 0,
      programPerformance: 0
    });
    setCascadeImpact({
      economicRipple: [],
      networkActivityChange: 0,
      exposureAdjustment: 0
    });
  }, []);
  
  const value = {
    // Scenarios
    POLICY_SCENARIOS,
    SCENARIO_CATEGORIES,
    MERCHANT_CATEGORIES,
    VALIDATION_STEPS,
    VALIDATION_RESULTS,
    
    // State
    activeScenario,
    policyParameters,
    validationTrace,
    currentTransaction,
    auditMetrics,
    cascadeImpact,
    
    // Actions
    selectScenario,
    setPolicyParameters,
    simulateValidation,
    updateAuditMetrics,
    calculateCascadeImpact,
    resetPolicy
  };
  
  return (
    <ProgrammablePolicyContext.Provider value={value}>
      {children}
    </ProgrammablePolicyContext.Provider>
  );
}

export function useProgrammablePolicy() {
  const context = useContext(ProgrammablePolicyContext);
  if (!context) {
    throw new Error('useProgrammablePolicy must be used within ProgrammablePolicyProvider');
  }
  return context;
}

export default ProgrammablePolicyContext;
